import { useEffect } from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";
import { FieldValues, UseFormRegister, UseFormWatch } from "react-hook-form";

type AutocompleteFieldOption = string | { label: string; id: string };

interface AutocompleteFieldProps {
  register?: UseFormRegister<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  onChange: (value: AutocompleteFieldOption[]) => void;
  options: AutocompleteFieldOption[];
  label: string;
  required?: boolean | string;
  id: string;
  sx?: any;
  multiple?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

export const AutocompleteField: React.FC<AutocompleteFieldProps> = ({
  options,
  onChange,
  label,
  register,
  id,
  required,
  watch,
  sx,
  multiple = false,
  disabled = false,
  error = false,
  helperText = "",
}) => {
  const value = watch(id);

  useEffect(() => {
    if (register) {
      register(id, { required });
    }
  }, []);

  const extended_options = multiple === true ? getOptionsWithSelectAll() : options;

  function getOptionsWithSelectAll() {
    if (!value || options.length === 0) return options;
    if (value.length === options.length) {
      return ["Deselect All", ...options];
    }
    return ["Select All", ...options];
  }

  return (
    <Autocomplete
      size="small"
      multiple={multiple}
      disableCloseOnSelect={multiple}
      limitTags={2}
      options={extended_options}
      value={value ?? (multiple === true ? [] : "")}
      onChange={(event, value) => {
        switch (true) {
          case !Array.isArray(value):
            return onChange(value);
          case value.includes("Select All"):
            return onChange(options);
          case value.includes("Deselect All"):
            return onChange([]);
          default:
            return onChange(value);
        }
      }}
      sx={sx}
      disabled={disabled}
      isOptionEqualToValue={(option, value) =>
        typeof option === "object" && typeof value === "object" ? option.id === value.id : option === value
      }
      renderTags={(value, getTagProps) => {
        return (
          <div>
            <Chip
              size="small"
              variant="outlined"
              label={typeof value[0] === "string" ? value[0] : value[0]?.label}
              {...getTagProps}
            />
            {value.length > 1 && <Chip size="small" label={`+${value.length - 1}`} />}
          </div>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={label} error={error} helperText={helperText} />
      )}
    />
  );
};
export default AutocompleteField;
